import React, { useEffect, useState } from 'react';
// TODO: Export these from UI Table
import { PaginationProps, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult, SortOrder } from 'antd/lib/table/interface';
import { PaginationData } from 'constants/interface';

const DEFAULT_CURRENT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_TOTAL = 0;
const DEFAULT_FILTER = {};
const ORDER_ASC = 'asc';
const ORDER_DESC = 'desc';

export interface PaginationFilterData extends Record<string, boolean | React.Key | undefined> {}

export interface PaginationInternalData extends PaginationProps {
  order?: SortOrder,
  orderBy?: string,
  filter: PaginationFilterData,
}

interface PaginationHook<T> {
  next(): void
  prev(): void
  getRemote(): PaginationData
  updateTotal(newValue: number): void
  updatePageSize(newValue: number): void
  getInternal(): PaginationInternalData
  setFilter(
    filterData: Record<string, (boolean | React.Key)[] | null>,
  ): void
  onTableChange(
    pageData: TablePaginationConfig,
    filterData: Record<string, FilterValue | null>,
    sortData: SorterResult<T> | SorterResult<T>[],
  ): void
  onSearch: (content: string) => void
  fetch(): void
}

interface PaginationOptions {
  initialTotal?: number
  defaultCurrent?: number
  defaultPageSize?: number
  filter?: PaginationFilterData
  order?: 'ascend' | 'descend'
  defaultOrder?: 'ascend' | 'descend'
  orderBy?: string
  search?: string
}

const sortDirectionMapper: Record<string, string> = {
  ascend: ORDER_ASC,
  descend: ORDER_DESC,
};

export const usePagination = <T>(
  fetch: (
    paginationData: PaginationData,
  ) => void | Promise<unknown>,
  opts?: PaginationOptions,
): PaginationHook<T> => {
  const [current, setCurrent] = useState(opts?.defaultCurrent || DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState(opts?.defaultPageSize || DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(opts?.initialTotal || DEFAULT_TOTAL);
  const [filter, setFilter] = useState(opts?.filter || DEFAULT_FILTER);
  const [order, setOrder] = useState<SortOrder | undefined>(opts?.order);
  const [orderBy, setOrderBy] = useState(opts?.orderBy);
  const [search, setSearch] = useState(opts?.search);

  const next = () => {
    const updatedValue = current === total ? total : current + 1;
    setCurrent(updatedValue);
  };

  const prev = () => {
    const updatedValue = current === 0 ? 0 : current - 1;
    setCurrent(updatedValue);
  };

  const getRemote = (): PaginationData => ({
    offset: (current - 1) * pageSize,
    limit: pageSize,
    filter,
    order: order ? sortDirectionMapper[order] : sortDirectionMapper[opts?.order || 'ascend'],
    orderBy,
    search,
  });

  const updateTotal = (newTotal: number) => {
    setTotal(newTotal);
  };

  const updatePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const internalFetch = () => {
    const paginationData: PaginationData = getRemote();
    fetch(paginationData);
  };

  const getInternal = (): PaginationInternalData => ({
    current,
    pageSize,
    total,
    order,
    orderBy,
    filter,
  });

  const onPageChange = (pageData: TablePaginationConfig) => {
    const { current: updatedCurrent, pageSize: updatedPageSize } = pageData;
    if (updatedCurrent) {
      setCurrent(updatedCurrent);
    }
    if (updatedPageSize) {
      setPageSize(updatedPageSize);
    }
  };

  const onFilterChange = (
    filterData: Record<string, (boolean | React.Key)[] | null>,
  ) => {
    const updatedFilter: PaginationFilterData = {
      ...filter,
    };
    Object.keys(filterData).forEach((columnFilter) => {
      const filterValueList = filterData[columnFilter];
      if (filterValueList) {
        // Set filter value or values
        const [filterValue] = filterValueList;
        updatedFilter[columnFilter] = filterValue;
      } else {
        // Clear filter
        updatedFilter[columnFilter] = undefined;
      }
    });
    setFilter(updatedFilter);
  };

  const sort = (sortData?: SorterResult<T>) => {
    if (sortData) {
      const { field: updatedField, order: updatedOrder } = sortData;
      const newOrderBy = updatedOrder ? updatedField?.toString() : undefined;
      setOrder(updatedOrder);
      setOrderBy(newOrderBy);
    }
  };

  const onSortChange = (sortData: SorterResult<T> | SorterResult<T>[]) => {
    if (sortData instanceof Array) {
      sort(sortData[0]);
    } else {
      sort(sortData);
    }
  };

  const onTableChange = (
    pageData: TablePaginationConfig,
    filterData: Record<string, FilterValue | null>,
    sortData: SorterResult<T> | SorterResult<T>[],
  ) => {
    onPageChange(pageData);
    onFilterChange(filterData);
    onSortChange(sortData);
  };

  const onSearch = (content: string) => {
    // TODO: check if should go back to first page when sorting
    // if (updatedOrder !== order || newOrderBy !== orderBy) {
    //   setCurrent(1);
    // }
    setSearch(content);
    setCurrent(1);
  };

  useEffect(() => {
    internalFetch();
  }, [current, pageSize, JSON.stringify(filter), order, orderBy, search]);

  return {
    next,
    prev,
    getRemote,
    updateTotal,
    updatePageSize,
    getInternal,
    setFilter: onFilterChange,
    onTableChange,
    onSearch,
    fetch: internalFetch,
  };
};
